import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';

/**
 * Validates a phone number field. Rejects empty input and requires a ten-digit North American pattern with an optionally parenthesised area code and optional space or hyphen separators.
 *
 * @param value - Phone number to validate.
 * @returns True when the phone number matches the accepted pattern.
 */
const checkPhoneNumber = (value: any) => {
  if (!value) {
    return false;
  }

  const isVail = /^((\([0-9]{3}\))|[0-9]{3})[\s\\-]?[\0-9]{3}[\s\\-]?[0-9]{4}$/.test(value);
  if (!isVail) {
    return false;
  }

  return true;
};

/**
 * Validates a fax number field. Rejects empty input and requires a ten-digit North American pattern with an optionally parenthesised area code and optional space or hyphen separators.
 *
 * @param value - Fax number to validate.
 * @returns True when the fax number matches the accepted pattern.
 */
const checkFaxNumber = (value: any) => {
  if (!value) {
    return false;
  }

  const isVail = /^((\([0-9]{3}\))|[0-9]{3})[\s\\-]?[\0-9]{3}[\s\\-]?[0-9]{4}$/.test(value);
  if (!isVail) {
    return false;
  }

  return true;
};

/**
 * Validates a password against the portal's complexity policy. Requires 10 to 128 characters including a special character or whitespace, a lowercase letter, an uppercase letter and a digit, while rejecting any character repeated three times in a row.
 *
 * @param password - Password to validate.
 * @returns True when the password satisfies every complexity rule.
 */
const checkPassword = (password: any) => {
  const regEx = /^(?=.*[!@#$%^&*\s])(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?!.*(.)\1{2}).{10,128}$/;
  if (!regEx.test(password)) {
    return false;
  }

  return true;
};

/**
 * Validates an email address. Trims and lowercases the input before testing it against a regex covering quoted local parts, dotted domains and bracketed IPv4 literals.
 *
 * @param email - Email address to validate.
 * @returns True when the address matches the email pattern.
 */
const validateEmail = (email: any) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).trim().toLowerCase());
};

/**
 * Validates a patient date of birth entered as MM/DD/YYYY. Requires the two-two-four digit shape and a strict dayjs parse, then rejects dates in the future or before 01/01/1900 by comparing start-of-day timestamps.
 *
 * @param dob - Date of birth string to validate.
 * @returns True when the date of birth is well formed and within range.
 */
const checkDob = (dob: any) => {
  const validDobCharacters = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.test(dob);
  if (!validDobCharacters || !dayjs(dob, 'MM/DD/YYYY', true).isValid()) {
    return false;
  }

  if (
    dayjs(dob, 'MM/DD/YYYY').startOf('d').valueOf() > dayjs(new Date()).startOf('d').valueOf() ||
    dayjs(dob, 'MM/DD/YYYY').startOf('d').valueOf() <
      dayjs(new Date('01/01/1900')).startOf('d').valueOf()
  ) {
    return false;
  }

  return true;
};

/**
 * Builds a field-to-message error map for a form descriptor list, marking every invalid entry with an Invalid value message. Required fields fail on blank values, an empty studyIdList, or a failing field-specific check for phone, fax, email, password or date of birth, while optional fields are only checked for phone and email format when they carry a value.
 *
 * @param checkNullData - List of field descriptors with field, value and isRequired.
 * @returns An object mapping each failing field name to its error message.
 */
const checkNull = (checkNullData: any) => {
  const errorObject: any = {};
  _.forEach(checkNullData, (data) => {
    if (data.isRequired) {
      if (!data.value || !data.value?.trim()) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (data.field === 'studyIdList' && data.value.length === 0) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (
        (data.field === 'phone1' || data.field === 'nightPhone') &&
        !checkPhoneNumber(data.value)
      ) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (data.field === 'fax' && !checkFaxNumber(data.value)) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if ((data.field === 'email' || data.field === 'replyEmail') && !validateEmail(data.value)) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (
        (data.field === 'password' || data.field === 'newPassword') &&
        !checkPassword(data.value)
      ) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (data.field === 'dob' && !checkDob(data.value)) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
    }
    if (!data.isRequired && data.value) {
      if (
        (data.field === 'nightPhone' ||
          data.field === 'phone2' ||
          data.field === 'phone' ||
          data.field === 'phone1' ||
          data.field === 'fax' ||
          data.field === 'secondaryPhone') &&
        !checkPhoneNumber(data.value)
      ) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
      if (
        (data.field === 'email' || data.field === 'secondaryEmail') &&
        !validateEmail(data.value)
      ) {
        _.assign(errorObject, { [data.field]: t`Invalid value` });
      }
    }
  });

  return errorObject;
};

export { checkNull, checkPhoneNumber, validateEmail };
